import { Link } from "@tanstack/react-router";
import { MessageCircle, Mail, Phone, ShieldCheck, AlertTriangle, Building2, DollarSign } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchSettings } from "@/lib/queries";
import { CONTACT } from "@/lib/contact";
import { trackWhatsAppClick } from "@/lib/analytics";
import logo from "@/assets/logo.webp";

// Computed once per bundle/process — stable between SSR and client renders
// of the same request to avoid hydration mismatch.
// Stable constant — avoid SSR/hydration mismatch with new Date().getFullYear()
const CURRENT_YEAR = 2026;

// `mobile: true` items render on every viewport; the rest only show on sm+ to
// keep the mobile footer compact (avoids a long stacked trust band).
const TRUST_ITEMS: { icon: typeof Building2; text: string; mobile?: boolean }[] = [
  { icon: AlertTriangle, text: "No somos agencia gubernamental", mobile: true },
  { icon: ShieldCheck, text: "No garantizamos aprobaciones de terceros", mobile: true },
  { icon: DollarSign, text: "Fees oficiales y costos variables pueden aplicar", mobile: true },
  { icon: Building2, text: "Operado por E&M Global Trade LLC" },
  { icon: MessageCircle, text: "Atención en español" },
  { icon: ShieldCheck, text: "Proceso organizado paso a paso" },
];

export function Footer() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: fetchSettings });
  const contact = settings?.contact ?? {};
  const legal = settings?.legal?.disclaimer ?? "";
  const whatsappUrl = contact.whatsapp_url ?? CONTACT.whatsappUrl;

  return (
    <footer className="mt-24 border-t border-hairline bg-surface">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 md:grid-cols-5 md:px-6">
        <div className="md:col-span-2">

          <div className="flex items-center gap-2">
            <img src={logo} alt="Truck Company Center" width={36} height={36} loading="lazy" decoding="async" className="h-9 w-9 rounded-md object-contain" />
            <span className="font-display text-base font-semibold tracking-tight text-foreground">
              Truck Company Center
            </span>
          </div>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">{legal}</p>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sitio</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/paquetes" className="text-foreground hover:text-primary">Paquetes</Link></li>
            <li><Link to="/servicios" className="text-foreground hover:text-primary">Servicios</Link></li>
            <li><Link to="/como-funciona" className="text-foreground hover:text-primary">Cómo funciona</Link></li>
            <li><Link to="/faq" className="text-foreground hover:text-primary">FAQ</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contacto</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href={whatsappUrl} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick("footer")} className="inline-flex items-center gap-2 text-foreground hover:text-primary">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a></li>
            {contact.email && (
              <li><a href={`mailto:${contact.email}`} className="inline-flex items-center gap-2 text-foreground hover:text-primary">
                <Mail className="h-4 w-4" /> {contact.email}
              </a></li>
            )}
            <li><a href={`tel:${CONTACT.phoneE164}`} className="inline-flex items-center gap-2 text-foreground hover:text-primary">
              <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
            </a></li>
          </ul>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Legal</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/terms" className="text-foreground hover:text-primary">Términos del servicio</Link></li>
            <li><Link to="/refund-policy" className="text-foreground hover:text-primary">Política de reembolso</Link></li>
            <li><Link to="/privacy-policy" className="text-foreground hover:text-primary">Política de privacidad</Link></li>
          </ul>
        </div>
      </div>

      {/* Trust band */}
      <div className="border-t border-hairline">
        <div className="mx-auto max-w-7xl px-4 py-4 md:px-6">
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {TRUST_ITEMS.map(({ icon: Icon, text, mobile }) => (
              <li
                key={text}
                className={`items-center gap-1.5 ${mobile ? "inline-flex" : "hidden sm:inline-flex"}`}
              >
                <Icon className="h-3.5 w-3.5 text-secondary" aria-hidden="true" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-hairline">
        <div className="mx-auto max-w-7xl px-4 py-6 text-xs text-muted-foreground md:px-6">
          <p>
            © {CURRENT_YEAR} Truck Company Center · Operado por E&amp;M Global Trade LLC.
            Prestamos asistencia administrativa y de organización. No somos
            agencia gubernamental, firma legal, firma contable ni aseguradora.
            Las aprobaciones, tiempos, requisitos y costos finales dependen de
            entidades externas, agencias, plataformas, aseguradoras y
            proveedores.
          </p>
        </div>
      </div>
    </footer>
  );
}
